import React from "react"
import moment from "moment"
import { notification } from "antd"
import api from "src/api"

export const getDefaultDebtPeriod = () => ({
  dateFrom: moment().subtract(29, "days").format("YYYY-MM-DD"),
  dateTo: moment().format("YYYY-MM-DD"),
})

// Минусовые балансы: сводка с прогнозом, долг по дням и список должников.
export function useDebts() {
  const [summary, setSummary] = React.useState(null)
  const [daily, setDaily] = React.useState([])
  const [period, setPeriod] = React.useState(getDefaultDebtPeriod)
  const [users, setUsers] = React.useState([])
  const [pagination, setPagination] = React.useState(null)
  const [status, setStatus] = React.useState("in_debt")
  // Фильтр по части телефона и почты, сортировка по колонке — на сервере.
  const [filters, setFilters] = React.useState({ phone: "", email: "" })
  const [sort, setSort] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isUsersLoading, setIsUsersLoading] = React.useState(false)
  // Номер последнего запроса списка: ответ на устаревший запрос не показываем.
  const usersRequest = React.useRef(0)

  React.useEffect(() => {
    loadSummary(period)
    loadUsers({ page: 1, countOnPage: 20 })
  }, [])

  function loadSummary(nextPeriod) {
    setIsLoading(true)
    const query = `date_from=${nextPeriod.dateFrom}&date_to=${nextPeriod.dateTo}`
    Promise.all([
      api.get("bonus-points/debts/summary", { useV2Api: true }),
      api.get(`bonus-points/debts/daily?${query}`, { useV2Api: true }),
    ])
      .then(([summaryResponse, dailyResponse]) => {
        setSummary(summaryResponse.data)
        setDaily(dailyResponse.data.rows ?? [])
      })
      .catch((error) => {
        notification.error({
          message: "Не удалось загрузить долги",
          description: error?.response?.data?.message,
        })
      })
      .finally(() => setIsLoading(false))
  }

  function loadUsers({
    page = 1,
    countOnPage = pagination?.pageSize ?? 20,
    nextStatus = status,
    nextFilters = filters,
    nextSort = sort,
  } = {}) {
    const requestId = ++usersRequest.current
    setIsUsersLoading(true)
    const params = new URLSearchParams({ page: String(page), countOnPage: String(countOnPage) })
    if (nextStatus) params.append("status", nextStatus)
    if (nextFilters.phone) params.append("phone", nextFilters.phone)
    if (nextFilters.email) params.append("email", nextFilters.email)
    if (nextSort) {
      params.append("sort", nextSort.field)
      params.append("direction", nextSort.order === "ascend" ? "asc" : "desc")
    }
    api
      .get(`bonus-points/debts/users?${params.toString()}`, { useV2Api: true })
      .then(({ data }) => {
        if (requestId !== usersRequest.current) return
        setUsers(data.items ?? [])
        setPagination({
          current: data.pagination.currentPage,
          pageSize: data.pagination.countOnPage,
          total: data.pagination.total,
        })
      })
      .finally(() => {
        if (requestId === usersRequest.current) setIsUsersLoading(false)
      })
  }

  function handlePeriod(nextPeriod) {
    setPeriod(nextPeriod)
    loadSummary(nextPeriod)
  }

  function handleStatus(nextStatus) {
    setStatus(nextStatus)
    loadUsers({ nextStatus })
  }

  function handleFilters(changed) {
    const nextFilters = { ...filters, ...changed }
    setFilters(nextFilters)
    loadUsers({ nextFilters })
  }

  function handleTableChange(pag, _tableFilters, sorter) {
    const nextSort = sorter?.order && sorter?.field ? { field: sorter.field, order: sorter.order } : null
    // Сменили сортировку — с первой страницы.
    const sortChanged = nextSort?.field !== sort?.field || nextSort?.order !== sort?.order
    setSort(nextSort)
    loadUsers({ page: sortChanged ? 1 : pag.current, countOnPage: pag.pageSize, nextSort })
  }

  function loadUser(userId) {
    return api.get(`bonus-points/debts/users/${userId}`, { useV2Api: true }).then(({ data }) => data)
  }

  return {
    summary,
    daily,
    period,
    users,
    pagination,
    status,
    filters,
    sort,
    isLoading,
    isUsersLoading,
    handlePeriod,
    handleStatus,
    handleFilters,
    handleTableChange,
    loadUser,
  }
}
